import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getLogoUrl } from '../utils/logoUrl';
import {
  FaFutbol, FaGlobe, FaChevronDown, FaInfoCircle, FaGavel,
  FaFileAlt, FaUserTie, FaClipboardList, FaExclamationTriangle
} from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import { teamsAPI } from '../services/api';

const DROPDOWN_CLOSE_DELAY_MS = 250;

const NavbarWithGroups = ({ isAdmin: isAdminProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [teams, setTeams] = useState([]);
  const dropdownCloseTimeoutRef = useRef(null);
  const { language, toggleLanguage, t } = useLanguage();
  // Tableau de bord / لوحة التحكم : visible uniquement si admin connecté (token présent)
  const isAdmin = !!localStorage.getItem('token');

  useEffect(() => {
    loadTeams();
  }, []);

  useEffect(() => {
    return () => {
      if (dropdownCloseTimeoutRef.current) clearTimeout(dropdownCloseTimeoutRef.current);
    };
  }, []);

  const loadTeams = async () => {
    try {
      const response = await teamsAPI.getAll();
      setTeams(response.data);
    } catch (error) {
      console.error('Erreur chargement équipes:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  // Organiser les équipes par groupe
  const teamsByGroup = (Array.isArray(teams) ? teams : []).reduce((acc, team) => {
    const group = team.group_number || 1;
    if (!acc[group]) acc[group] = [];
    acc[group].push(team);
    return acc;
  }, {});

  const infoTiles = [
    { id: 1, label: t('about'), link: '/about', icon: FaInfoCircle, color1: '#4facfe', color2: '#00f2fe' },
    { id: 2, label: t('committees'), link: '/committees', icon: FaUserTie, color1: '#667eea', color2: '#764ba2' },
    { id: 3, label: t('regulations'), link: '/regulations', icon: FaFileAlt, color1: '#f093fb', color2: '#f5576c' },
    { id: 4, label: t('laws'), link: '/laws', icon: FaGavel, color1: '#84fab0', color2: '#8fd3f4' },
    { id: 5, label: t('designations'), link: '/designations', icon: FaClipboardList, color1: '#ff9a9e', color2: '#fecfef' },
    { id: 6, label: t('discipline'), link: '/discipline', icon: FaExclamationTriangle, color1: '#fa709a', color2: '#fee140' }
  ];

  const menuStructure = {
    ar: [
      { id: 'home', label: '🏠 الرئيسية', link: '/' },
      {
        id: 'infos',
        label: 'ℹ️ المعلومات',
        megaMenuTiles: true
      },
      {
        id: 'competitions',
        label: '⚽ البطولات',
        submenu: [
          { label: 'بطولة المستوى الثاني', link: '/championship' },
          { label: 'تركيبة المجموعات', link: '/groups' },
          { label: 'ترتيب الفرق', link: '/standings' },
          { label: 'جدول المباريات', link: '/calendar' },
          { label: 'النتائج', link: '/results' }
        ]
      },
      { id: 'teams', label: '👥 الفرق', megaMenu: true },
      {
        id: 'news',
        label: '📰 الأخبار',
        submenu: [
          { label: 'أخبار الرابطة', link: '/news' },
          { label: 'القوانين واللوائح', link: '/communiques' },
          { label: 'مستجدات الفرق', link: '/club-news' },
          { label: 'قرارات لجنة التأديب والروح الرياضية', link: '/discipline' }
        ]
      },
      {
        id: 'statistics',
        label: '📊 الإحصائيات',
        submenu: [
          { label: 'ترتيب الفرق', link: '/standings' },
          { label: 'الهدافين', link: '/statistics' },
          { label: 'البطاقات', link: '/statistics' }
        ]
      },
      {
        id: 'media',
        label: '🖼️ ملتميديا',
        link: '/media'
      },
      {
        id: 'usefulLinks',
        label: '🔗 روابط مفيدة',
        link: '/useful-links'
      }
    ],
    fr: [
      { id: 'home', label: '🏠 Accueil', link: '/' },
      {
        id: 'infos',
        label: 'ℹ️ Infos Tiles',
        megaMenuTiles: true
      },
      {
        id: 'competitions',
        label: '⚽ Compétitions',
        submenu: [
          { label: 'Championnat N2', link: '/championship' },
          { label: 'Composition des Groupes', link: '/groups' },
          { label: 'Classement', link: '/standings' },
          { label: 'Calendrier', link: '/calendar' },
          { label: 'Résultats', link: '/results' }
        ]
      },
      { id: 'teams', label: '👥 Équipes', megaMenu: true },
      {
        id: 'news',
        label: '📰 Actualités',
        submenu: [
          { label: 'Actualités Ligue', link: '/news' },
          { label: 'Lois et Règlements', link: '/communiques' },
          { label: 'Actualités Clubs', link: '/club-news' },
          { label: 'Décisions Discipline et Esprit Sportif', link: '/discipline' }
        ]
      },
      {
        id: 'statistics',
        label: '📊 Statistiques',
        submenu: [
          { label: 'Classement', link: '/standings' },
          { label: 'Buteurs', link: '/statistics' },
          { label: 'Cartons', link: '/statistics' }
        ]
      },
      {
        id: 'media',
        label: '🖼️ Média',
        link: '/media'
      },
      {
        id: 'usefulLinks',
        label: '🔗 Infos utiles',
        link: '/useful-links'
      }
    ]
  };

  const currentMenu = menuStructure[language];

  const handleMouseEnter = (menuId) => {
    if (dropdownCloseTimeoutRef.current) {
      clearTimeout(dropdownCloseTimeoutRef.current);
      dropdownCloseTimeoutRef.current = null;
    }
    setOpenDropdown(menuId);
  };

  const handleMouseLeave = () => {
    dropdownCloseTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
      dropdownCloseTimeoutRef.current = null;
    }, DROPDOWN_CLOSE_DELAY_MS);
  };

  return (
    <nav style={styles.nav}>
      <div style={styles.container}>
        <Link to="/" style={styles.brand} title={language === 'ar' ? 'الرئيسية' : 'Accueil'}>
          <div style={styles.logoCarreau}>
            <img
              src="/images/logo.png"
              alt="Ligue"
              style={styles.logo}
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
              }}
            />
            <FaFutbol style={{ ...styles.brandIcon, display: 'none' }} />
          </div>
        </Link>

        <div className="navbar-menu-desktop" style={styles.menu}>
          {currentMenu.map(item => (
            item.megaMenuTiles ? (
              <div
                key={item.id}
                style={styles.menuItem}
                onMouseEnter={() => handleMouseEnter(item.id)}
                onMouseLeave={handleMouseLeave}
              >
                <button style={styles.menuButton}>
                  {item.label} <FaChevronDown style={styles.chevron} />
                </button>
                {openDropdown === item.id && (
                  <div style={styles.megaMenuTilesWrap}>
                    <div style={styles.tilesGrid}>
                      {infoTiles.map(tile => (
                        <Link
                          key={tile.id}
                          to={tile.link}
                          style={{
                            ...styles.navTile,
                            background: `linear-gradient(135deg, ${tile.color1}, ${tile.color2})`
                          }}
                          onClick={() => setOpenDropdown(null)}
                        >
                          <tile.icon style={styles.navTileIcon} />
                          <span style={styles.navTileLabel}>{tile.label}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : item.megaMenu ? (
              // Mega Menu pour les équipes
              <div
                key={item.id}
                style={styles.menuItem}
                onMouseEnter={() => handleMouseEnter(item.id)}
                onMouseLeave={handleMouseLeave}
              >
                <button style={styles.menuButton}>
                  {item.label} <FaChevronDown style={styles.chevron} />
                </button>

                {openDropdown === item.id && (
                  <div style={styles.megaMenu}>
                    <div style={styles.megaMenuContent}>
                      {[1, 2, 3, 4, 5, 6].map(groupNum => {
                        const groupTeams = teamsByGroup[groupNum] || [];
                        return (
                          <div key={groupNum} style={styles.megaMenuColumn}>
                            <h3 style={styles.megaMenuTitle}>
                              {language === 'ar' ? 'المجموعة' : 'Groupe'} {groupNum}
                            </h3>
                            <div style={styles.teamsList}>
                              {groupTeams.slice(0, 10).map(team => (
                                <Link
                                  key={team.id}
                                  to={`/teams/${team.id}`}
                                  style={styles.teamLink}
                                  onClick={() => setOpenDropdown(null)}
                                >
                                  {getLogoUrl(team.logo_url) && (
                                    <img
                                      src={getLogoUrl(team.logo_url)}
                                      alt={team.name}
                                      style={styles.teamLinkLogo}
                                      onError={(e) => e.target.style.display = 'none'}
                                    />
                                  )}
                                  <span style={styles.teamLinkName}>{team.name}</span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={styles.megaMenuFooter}>
                      <Link to="/teams" style={styles.megaMenuFooterLink} onClick={() => setOpenDropdown(null)}>
                        {language === 'ar' ? 'عرض جميع الفرق ←' : 'Voir toutes les équipes →'}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : item.submenu ? (
              // Dropdown normal
              <div
                key={item.id}
                style={styles.menuItem}
                onMouseEnter={() => handleMouseEnter(item.id)}
                onMouseLeave={handleMouseLeave}
              >
                <button style={styles.menuButton}>
                  {item.label} <FaChevronDown style={styles.chevron} />
                </button>

                {openDropdown === item.id && (
                  <div style={styles.dropdown}>
                    {item.submenu.map((subItem, index) => (
                      <Link
                        key={index}
                        to={subItem.link}
                        style={{
                          ...styles.dropdownItem,
                          ...(isActive(subItem.link) ? styles.dropdownItemActive : {})
                        }}
                        onClick={() => setOpenDropdown(null)}
                      >
                        {subItem.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Lien simple
              <Link
                key={item.id}
                to={item.link}
                style={{
                  ...styles.link,
                  ...(isActive(item.link) ? styles.linkActive : {})
                }}
              >
                {item.label}
              </Link>
            )
          ))}
        </div>

        <div className="navbar-right-desktop" style={styles.rightSection}>
          <Link to="/live" style={{ ...styles.liveLink, ...(isActive('/live') ? styles.linkActive : {}) }}>
            🔴 {t('live')}
          </Link>

          <button onClick={toggleLanguage} style={styles.langBtn}>
            <FaGlobe /> {language === 'ar' ? 'Français' : 'العربية'}
          </button>

          {isAdmin && (
            <>
              <Link to="/admin/dashboard" style={styles.adminLink}>
                {t('dashboard')}
              </Link>
              <button onClick={handleLogout} style={styles.logoutBtn}>
                {t('logout')}
              </button>
            </>
          )}
        </div>

        <button
          className="navbar-mobile-btn"
          style={styles.mobileMenuBtn}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Menu"
        >
          ☰
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="navbar-mobile-drawer" style={styles.mobileMenuOverlay} onClick={() => setMobileMenuOpen(false)}>
          <div className="navbar-mobile-panel" style={styles.mobileMenu} onClick={(e) => e.stopPropagation()}>
            {currentMenu.map(item => (
              <div key={item.id}>
                {item.megaMenuTiles ? (
                  <>
                    <div style={styles.mobileMenuTitle}>{item.label}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '10px' }}>
                      {infoTiles.map(tile => (
                        <Link
                          key={tile.id}
                          to={tile.link}
                          style={{
                            ...styles.mobileTile,
                            background: `linear-gradient(135deg, ${tile.color1}, ${tile.color2})`
                          }}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <tile.icon size={20} />
                          <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{tile.label}</span>
                        </Link>
                      ))}
                    </div>
                  </>
                ) : item.megaMenu ? (
                  <>
                    <div style={styles.mobileMenuTitle}>{item.label}</div>
                    {[1, 2, 3, 4, 5, 6].map(groupNum => {
                      const groupTeams = teamsByGroup[groupNum] || [];
                      return (
                        <div key={groupNum} style={styles.mobileGroupSection}>
                          <div style={styles.mobileGroupTitle}>
                            {language === 'ar' ? 'المجموعة' : 'Groupe'} {groupNum}
                          </div>
                          {groupTeams.slice(0, 5).map(team => (
                            <Link
                              key={team.id}
                              to={`/teams/${team.id}`}
                              style={styles.mobileSubLink}
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              • {team.name}
                            </Link>
                          ))}
                        </div>
                      );
                    })}
                  </>
                ) : item.submenu ? (
                  <>
                    <div style={styles.mobileMenuTitle}>{item.label}</div>
                    {item.submenu.map((subItem, index) => (
                      <Link
                        key={index}
                        to={subItem.link}
                        style={styles.mobileSubLink}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        • {subItem.label}
                      </Link>
                    ))}
                  </>
                ) : (
                  <Link
                    to={item.link}
                    style={styles.mobileLink}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}

            <Link to="/live" style={styles.mobileLink} onClick={() => setMobileMenuOpen(false)}>
              🔴 {t('live')}
            </Link>

            <button onClick={toggleLanguage} style={styles.mobileLink}>
              <FaGlobe /> {language === 'ar' ? 'Français' : 'عربي'}
            </button>

            {isAdmin && (
              <>
                <Link to="/admin/dashboard" style={styles.mobileLink} onClick={() => setMobileMenuOpen(false)}>
                  ⚙️ {t('dashboard')}
                </Link>
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} style={styles.mobileLink}>
                  🚪 {t('logout')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

const styles = {
  nav: {
    background: 'white',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 1000
  },
  container: {
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '10px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap'
  },
  brand: {
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    flexShrink: 0,
    color: 'inherit'
  },
  logoCarreau: {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: 8,
    background: 'rgba(196, 30, 58, 0.08)',
    border: '1px solid rgba(196, 30, 58, 0.2)'
  },
  logo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block'
  },
  brandIcon: {
    fontSize: 22,
    color: '#c41e3a'
  },
  menu: {
    display: 'flex',
    gap: '5px',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    flexWrap: 'wrap'
  },
  menuItem: {
    position: 'relative'
  },
  menuButton: {
    background: 'transparent',
    border: 'none',
    color: '#333',
    fontWeight: '600',
    fontSize: '14px',
    padding: '6px 10px',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.3s',
    whiteSpace: 'nowrap',
    letterSpacing: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  },
  chevron: {
    fontSize: '10px'
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: '0',
    right: 'auto',
    background: 'white',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    borderRadius: '8px',
    marginTop: '2px',
    paddingTop: '4px',
    minWidth: '200px',
    zIndex: 1000,
    overflow: 'hidden',
    animation: 'fadeIn 0.2s ease'
  },
  dropdownItem: {
    display: 'block',
    padding: '12px 20px',
    color: '#333',
    textDecoration: 'none',
    fontSize: '14px',
    transition: 'all 0.3s',
    borderBottom: '1px solid #f0f0f0'
  },
  dropdownItemActive: {
    background: 'rgba(196, 30, 58, 0.1)',
    color: '#c41e3a',
    fontWeight: 'bold'
  },
  megaMenu: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'white',
    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
    borderRadius: '12px',
    marginTop: '5px',
    width: 'min(900px, 90vw)',
    maxWidth: '90vw',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  megaMenuContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
    padding: '25px',
    maxHeight: '500px',
    overflowY: 'auto'
  },
  megaMenuColumn: {
    borderRight: '1px solid #f0f0f0'
  },
  megaMenuTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#c41e3a',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '2px solid #c41e3a'
  },
  teamsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  teamLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: '6px',
    textDecoration: 'none',
    color: '#333',
    fontSize: '13px',
    transition: 'all 0.2s'
  },
  teamLinkLogo: {
    width: '20px',
    height: '20px',
    objectFit: 'contain'
  },
  teamLinkName: {
    fontSize: '12px',
    lineHeight: '1.2'
  },
  megaMenuFooter: {
    padding: '15px 25px',
    borderTop: '1px solid #f0f0f0',
    background: '#f8f9fa',
    textAlign: 'center'
  },
  megaMenuFooterLink: {
    color: '#c41e3a',
    textDecoration: 'none',
    fontWeight: 'bold',
    fontSize: '14px'
  },
  rightSection: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    minWidth: 0,
    flexShrink: 0,
    justifyContent: 'flex-end',
    flexWrap: 'wrap'
  },
  liveLink: {
    textDecoration: 'none',
    color: '#333',
    fontWeight: '600',
    fontSize: '14px',
    padding: '6px 10px',
    borderRadius: '6px',
    transition: 'all 0.3s',
    whiteSpace: 'nowrap',
    letterSpacing: '0'
  },
  link: {
    textDecoration: 'none',
    color: '#333',
    fontWeight: '600',
    fontSize: '14px',
    padding: '6px 10px',
    borderRadius: '6px',
    transition: 'all 0.3s',
    whiteSpace: 'nowrap',
    letterSpacing: '0'
  },
  linkActive: {
    background: 'rgba(196, 30, 58, 0.1)',
    color: '#c41e3a'
  },
  adminLink: {
    textDecoration: 'none',
    background: '#f5f5f5',
    color: '#333',
    padding: '6px 15px',
    borderRadius: '6px',
    fontWeight: '600',
    transition: 'all 0.3s',
    fontSize: '14px'
  },
  loginBtn: {
    textDecoration: 'none',
    background: '#c41e3a',
    color: 'white',
    padding: '8px 20px',
    borderRadius: '6px',
    fontWeight: 'bold',
    transition: 'all 0.3s',
    fontSize: '14px'
  },
  logoutBtn: {
    background: '#666',
    color: 'white',
    border: 'none',
    padding: '8px 20px',
    borderRadius: '6px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s',
    fontSize: '14px'
  },
  langBtn: {
    background: '#f5f5f5',
    color: '#333',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '6px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    letterSpacing: '0'
  },
  mobileMenuBtn: {
    display: 'none',
    background: '#c41e3a',
    color: 'white',
    border: 'none',
    padding: '10px 15px',
    borderRadius: '8px',
    fontSize: '24px',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center'
  },
  mobileMenuOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    background: 'rgba(0,0,0,0.4)',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column'
  },
  mobileMenu: {
    flexDirection: 'column',
    background: 'white',
    padding: '20px',
    gap: '10px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    maxHeight: '85vh',
    overflowY: 'auto',
    width: '100%',
    maxWidth: '100%',
    display: 'flex'
  },
  mobileLink: {
    textDecoration: 'none',
    color: '#333',
    padding: '12px',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '16px',
    background: '#f5f5f5',
    textAlign: 'center',
    border: 'none',
    width: '100%',
    cursor: 'pointer'
  },
  mobileMenuTitle: {
    padding: '12px',
    fontWeight: 'bold',
    fontSize: '16px',
    color: '#c41e3a',
    background: '#f9f9f9',
    borderRadius: '8px',
    marginTop: '10px'
  },
  mobileSubLink: {
    textDecoration: 'none',
    color: '#666',
    padding: '10px 12px 10px 25px',
    borderRadius: '8px',
    fontSize: '14px',
    background: 'white',
    display: 'block',
    border: '1px solid #f0f0f0',
    marginTop: '5px'
  },
  mobileGroupSection: {
    marginTop: '10px',
    paddingLeft: '10px'
  },
  mobileGroupTitle: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#c41e3a',
    padding: '8px 12px',
    background: '#fff5f5',
    borderRadius: '6px',
    marginBottom: '5px'
  },
  megaMenuTilesWrap: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'white',
    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
    borderRadius: '12px',
    marginTop: '5px',
    width: 'min(500px, 90vw)',
    maxWidth: '90vw',
    zIndex: 1000,
    padding: '20px',
    animation: 'fadeIn 0.2s ease'
  },
  tilesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px'
  },
  navTile: {
    padding: '15px',
    borderRadius: '12px',
    textDecoration: 'none',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    transition: 'transform 0.2s'
  },
  navTileIcon: {
    fontSize: '20px'
  },
  navTileLabel: {
    fontWeight: 'bold',
    fontSize: '14px'
  },
  mobileTile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px',
    padding: '10px',
    borderRadius: '10px',
    color: 'white',
    textDecoration: 'none'
  }
};

// Animation CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .teamLink:hover {
    background: rgba(196, 30, 58, 0.05) !important;
    padding-left: 12px !important;
  }

  .nav-tile:hover {
    transform: scale(1.05);
  }

`;
document.head.appendChild(styleSheet);

export default NavbarWithGroups;
